import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useLocalRuntime, AssistantRuntimeProvider } from "@assistant-ui/react";
import { NO_ACTIVITY, type AgentActivity } from "./agentActivity";
import { AgentActivityContext } from "./AgentActivityContext";
import { createAgentTransport } from "./transport";
import type { ActiveInputComponent } from "./transport";
import { GenUIInputContext } from "./genui/GenUIInputContext";
import { useSessionWarmup } from "./warmup";
import type { AgentProviderProps, MessageFeedbackContextValue } from "./AgentProvider.types";

const MessageFeedbackContext = createContext<MessageFeedbackContextValue | null>(null);

/**
 * Feedback submission for the active session; the provider owns the session id, so callers
 * only pass the backend message id and rating. Throws outside `<AgentProvider>`.
 */
export function useMessageFeedback(): MessageFeedbackContextValue {
  const value = useContext(MessageFeedbackContext);
  if (!value) throw new Error("useMessageFeedback must be used within <AgentProvider>");
  return value;
}

export function AgentProvider({
  children,
  api,
  activeSession,
  initialMessages,
  getConversation,
  getMrn,
  clientApp,
  clientAppVersion,
  clientAppDesktopVersion,
}: AgentProviderProps) {
  const generatedSessionId = useMemo(() => crypto.randomUUID(), []);
  const sessionId = activeSession ?? generatedSessionId;

  // Pay the agent's cold start while the user reads the welcome screen, on the same pinned
  // runtime session the first turn will use.
  useSessionWarmup(api, sessionId, { clientApp, clientAppVersion, clientAppDesktopVersion });

  // Interactive GenUI components are diverted here rather than into the message
  // list; the composer-adjacent GenUIInputArea renders whatever is active.
  const [activeInput, setActiveInput] = useState<ActiveInputComponent | null>(null);
  const showInput = useCallback((c: ActiveInputComponent) => setActiveInput(c), []);
  const dismissInput = useCallback(() => setActiveInput(null), []);

  const [activities, setActivities] = useState<readonly AgentActivity[]>(NO_ACTIVITY);

  const adapter = useMemo(
    () =>
      createAgentTransport({
        api,
        sessionId,
        getConversation,
        getMrn,
        onInputComponent: showInput,
        onActivity: setActivities,
        clientApp,
        clientAppVersion,
        clientAppDesktopVersion,
      }),
    [
      api,
      sessionId,
      getConversation,
      getMrn,
      showInput,
      clientApp,
      clientAppVersion,
      clientAppDesktopVersion,
    ],
  );
  const runtime = useLocalRuntime(adapter, { initialMessages });

  const inputValue = useMemo(
    () => ({ activeInput, showInput, dismissInput }),
    [activeInput, showInput, dismissInput],
  );

  // Resolves on server confirmation; a rejection leaves the displayed rating untouched.
  const feedbackContext = useMemo<MessageFeedbackContextValue>(
    () => ({
      sessionId,
      submitFeedback: (messageId, type) =>
        api
          .put(
            `sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`,
            { json: { type } },
          )
          .then(() => undefined),
    }),
    [api, sessionId],
  );

  return (
    <MessageFeedbackContext.Provider value={feedbackContext}>
      <AssistantRuntimeProvider runtime={runtime}>
        <AgentActivityContext.Provider value={activities}>
          <GenUIInputContext.Provider value={inputValue}>{children}</GenUIInputContext.Provider>
        </AgentActivityContext.Provider>
      </AssistantRuntimeProvider>
    </MessageFeedbackContext.Provider>
  );
}
